import { getMarket } from '../i18n';

const TILE_SIZE = 256;

function bound(value: number, opt_min: number | null, opt_max: number | null) {
  if (opt_min !== null) value = Math.max(value, opt_min);
  if (opt_max !== null) value = Math.min(value, opt_max);
  return value;
}

function degreesToRadians(deg: number) {
  return deg * (Math.PI / 180);
}

function radiansToDegrees(rad: number) {
  return rad / (Math.PI / 180);
}

class MercatorProjection {
  pixelOrigin_ = new google.maps.Point(TILE_SIZE / 2, TILE_SIZE / 2);
  pixelsPerLonDegree_ = TILE_SIZE / 360;
  pixelsPerLonRadian_ = TILE_SIZE / (2 * Math.PI);

  fromLatLngToPoint(latLng: LatLng, opt_point?: google.maps.Point) {
    const point = opt_point || new google.maps.Point(0, 0);
    const origin = this.pixelOrigin_;

    point.x = origin.x + latLng.lng() * this.pixelsPerLonDegree_;

    const siny = bound(Math.sin(degreesToRadians(latLng.lat())), -0.9999, 0.9999);
    point.y = origin.y + 0.5 * Math.log((1 + siny) / (1 - siny)) * -this.pixelsPerLonRadian_;
    return point;
  }

  fromPointToLatLng(point: google.maps.Point) {
    const origin = this.pixelOrigin_;
    const lng = (point.x - origin.x) / this.pixelsPerLonDegree_;
    const latRadians = (point.y - origin.y) / -this.pixelsPerLonRadian_;
    const lat = radiansToDegrees(2 * Math.atan(Math.exp(latRadians)) - Math.PI / 2);
    return new google.maps.LatLng(lat, lng);
  }
}

interface LatLng {
  lng: () => number;
  lat: () => number;
}

export const getPixelRadius = (map: google.maps.Map, marker: google.maps.Circle) => {
  if (!map) throw 'Missing maps';
  const zoom = map.getZoom();
  const center = map.getCenter();
  if (zoom === undefined || center === undefined) throw 'Error google maps';
  const tiles = 1 << zoom;
  const moved = google.maps.geometry.spherical.computeOffset(center, 10000, 90);
  const projection = new MercatorProjection();
  const initCoord = projection.fromLatLngToPoint(center);
  const endCoord = projection.fromLatLngToPoint(moved);
  const initPoint = new google.maps.Point(initCoord.x * tiles, initCoord.y * tiles);
  const endPoint = new google.maps.Point(endCoord.x * tiles, endCoord.y * tiles);
  const pixelsPerMeter = Math.abs(initPoint.x - endPoint.x) / 10000.0;
  const totalPixelSize = Math.ceil(marker.getRadius() * pixelsPerMeter);

  return totalPixelSize;
};

export const getLocationSetSE = () => [
  {
    lat: 59.33,
    lng: 18.07,
    city: 'Stockholm',
    name: 'Stockholm',
    radius: 50,
  },
  {
    lat: 57.71,
    lng: 11.97,
    city: 'Göteborg',
    name: 'Göteborg',
    radius: 50,
  },
  {
    lat: 56.05,
    lng: 12.69,
    city: 'Helsingborg',
    name: 'Helsingborg',
    radius: 50,
  },
  {
    lat: 57.78,
    lng: 14.16,
    city: 'Jönköping',
    name: 'Jönköping',
    radius: 50,
  },
  {
    lat: 55.6,
    lng: 13,
    city: 'Malmö',
    name: 'Malmö',
    radius: 50,
  },
  {
    lat: 59.28,
    lng: 15.21,
    city: 'Örebro',
    name: 'Örebro',
    radius: 50,
  },
  {
    lat: 58.58,
    lng: 16.19,
    city: 'Norrköping',
    name: 'Norrköping',
    radius: 50,
  },
  {
    lat: 58.41,
    lng: 15.62,
    city: 'Linköping',
    name: 'Linköping',
    radius: 50,
  },
  {
    lat: 59.86,
    lng: 17.64,
    city: 'Uppsala',
    name: 'Uppsala',
    radius: 50,
  },
  {
    lat: 59.61,
    lng: 16.54,
    city: 'Västerås',
    name: 'Västerås',
    radius: 50,
  },
  {
    lat: 56.67,
    lng: 12.85,
    city: 'Halmstad',
    name: 'Halmstad',
    radius: 50,
  },
  {
    lat: 56.87,
    lng: 14.81,
    city: 'Växjö',
    name: 'Växjö',
    radius: 50,
  },
  {
    lat: 59.37,
    lng: 16.51,
    city: 'Eskilstuna',
    name: 'Eskilstuna',
    radius: 50,
  },
  {
    lat: 56.66,
    lng: 16.36,
    city: 'Kalmar',
    name: 'Kalmar',
    radius: 50,
  },
  {
    lat: 56.16,
    lng: 15.58,
    city: 'Karlskrona',
    name: 'Karlskrona',
    radius: 50,
  },
  {
    lat: 59.38,
    lng: 13.49,
    city: 'Karlstad',
    name: 'Karlstad',
    radius: 50,
  },
  {
    lat: 56.03,
    lng: 14.18,
    city: 'Kristianstad',
    name: 'Kristianstad',
    radius: 50,
  },
  {
    lat: 62.39,
    lng: 17.31,
    city: 'Sundsvall',
    name: 'Sundsvall',
    radius: 50,
  },
  {
    lat: 63.83,
    lng: 20.25,
    city: 'Umeå',
    name: 'Umeå',
    radius: 50,
  },
  {
    lat: 57.1,
    lng: 12.25,
    city: 'Varberg',
    name: 'Varberg',
    radius: 50,
  },
  {
    lat: 57.72,
    lng: 12.93,
    city: 'Borås',
    name: 'Borås',
    radius: 50,
  },
  {
    lat: 56.9,
    lng: 12.49,
    city: 'Falkenberg',
    name: 'Falkenberg',
    radius: 50,
  },
  {
    lat: 60.67,
    lng: 17.14,
    city: 'Gävle',
    name: 'Gävle',
    radius: 50,
  },
  {
    lat: 65.58,
    lng: 22.14,
    city: 'Luleå',
    name: 'Luleå',
    radius: 50,
  },
  {
    lat: 55.7,
    lng: 13.19,
    city: 'Lund',
    name: 'Lund',
    radius: 50,
  },
  {
    lat: 57.04,
    lng: 16.44,
    city: 'Mönsterås',
    name: 'Mönsterås',
    radius: 50,
  },
  {
    lat: 58.35,
    lng: 11.93,
    city: 'Uddevalla',
    name: 'Uddevalla',
    radius: 50,
  },
  {
    lat: 57.76,
    lng: 16.64,
    city: 'Västervik',
    name: 'Västervik',
    radius: 50,
  },
  {
    lat: 55.43,
    lng: 13.83,
    city: 'Ystad',
    name: 'Ystad',
    radius: 50,
  },
  {
    lat: 63.17,
    lng: 14.64,
    city: 'Östersund',
    name: 'Östersund',
    radius: 50,
  },
  {
    lat: 60.49,
    lng: 15.42,
    city: 'Borlänge',
    name: 'Borlänge',
    radius: 50,
  },
  {
    lat: 67.85,
    lng: 20.22,
    city: 'Kiruna',
    name: 'Kiruna',
    radius: 50,
  },
  {
    lat: 58.75,
    lng: 17.01,
    city: 'Nyköping',
    name: 'Nyköping',
    radius: 50,
  },
  {
    lat: 57.26,
    lng: 16.45,
    city: 'Oskarshamn',
    name: 'Oskarshamn',
    radius: 50,
  },
  {
    lat: 59.61,
    lng: 17.72,
    city: 'Sigtuna',
    name: 'Sigtuna',
    radius: 50,
  },
  {
    lat: 64.75,
    lng: 20.95,
    city: 'Skellefteå',
    name: 'Skellefteå',
    radius: 50,
  },
  {
    lat: 58.39,
    lng: 13.84,
    city: 'Skövde',
    name: 'Skövde',
    radius: 50,
  },
  {
    lat: 58.28,
    lng: 12.29,
    city: 'Trollhättan',
    name: 'Trollhättan',
    radius: 50,
  },
  {
    lat: 57.93,
    lng: 12.53,
    city: 'Alingsås',
    name: 'Alingsås',
    radius: 50,
  },
  {
    lat: 56.42,
    lng: 12.85,
    city: 'Båstad',
    name: 'Båstad',
    radius: 50,
  },
];

export const getLocationSetNO = () => [
  {
    lat: 59.91,
    lng: 10.75,
    city: 'Oslo',
    name: 'Oslo',
    radius: 100,
  },
  {
    lat: 60.39,
    lng: 5.32,
    city: 'Bergen',
    name: 'Bergen',
    radius: 100,
  },
  {
    lat: 63.43,
    lng: 10.4,
    city: 'Trondheim',
    name: 'Trondheim',
    radius: 100,
  },
  {
    lat: 58.97,
    lng: 5.73,
    city: 'Stavanger',
    name: 'Stavanger',
    radius: 100,
  },
  {
    lat: 58.16,
    lng: 8.0,
    city: 'Kristiansand',
    name: 'Kristiansand',
    radius: 100,
  },
  {
    lat: 59.21,
    lng: 10.95,
    city: 'Fredrikstad',
    name: 'Fredrikstad',
    radius: 100,
  },
  {
    lat: 59.74,
    lng: 10.2,
    city: 'Drammen',
    name: 'Drammen',
    radius: 100,
  },
  {
    lat: 59.2,
    lng: 9.6,
    city: 'Skien',
    name: 'Skien',
    radius: 100,
  },
  {
    lat: 69.65,
    lng: 18.96,
    city: 'Tromsø',
    name: 'Tromsø',
    radius: 100,
  },
  {
    lat: 62.47,
    lng: 6.15,
    city: 'Ålesund',
    name: 'Ålesund',
    radius: 100,
  },
  {
    lat: 59.43,
    lng: 10.66,
    city: 'Moss',
    name: 'Moss',
    radius: 100,
  },
  {
    lat: 59.14,
    lng: 9.66,
    city: 'Porsgrunn',
    name: 'Porsgrunn',
    radius: 100,
  },
  {
    lat: 67.28,
    lng: 14.4,
    city: 'Bodø',
    name: 'Bodø',
    radius: 100,
  },
  {
    lat: 58.46,
    lng: 8.77,
    city: 'Arendal',
    name: 'Arendal',
    radius: 100,
  },
  {
    lat: 60.79,
    lng: 11.07,
    city: 'Hamar',
    name: 'Hamar',
    radius: 100,
  },
  {
    lat: 59.05,
    lng: 10.03,
    city: 'Larvik',
    name: 'Larvik',
    radius: 100,
  },
  {
    lat: 59.12,
    lng: 11.38,
    city: 'Halden',
    name: 'Halden',
    radius: 100,
  },
  {
    lat: 61.11,
    lng: 10.47,
    city: 'Lillehammer',
    name: 'Lillehammer',
    radius: 100,
  },
  {
    lat: 62.73,
    lng: 7.16,
    city: 'Molde',
    name: 'Molde',
    radius: 100,
  },
  {
    lat: 59.67,
    lng: 9.65,
    city: 'Kongsberg',
    name: 'Kongsberg',
    radius: 100,
  },
  {
    lat: 68.8,
    lng: 16.54,
    city: 'Harstad',
    name: 'Harstad',
    radius: 100,
  },
  {
    lat: 60.8,
    lng: 10.7,
    city: 'Gjøvik',
    name: 'Gjøvik',
    radius: 100,
  },
  {
    lat: 59.28,
    lng: 11.11,
    city: 'Sarpsborg',
    name: 'Sarpsborg',
    radius: 100,
  },
  {
    lat: 59.13,
    lng: 10.22,
    city: 'Sandefjord',
    name: 'Sandefjord',
    radius: 100,
  },
  {
    lat: 63.11,
    lng: 7.73,
    city: 'Kristiansund',
    name: 'Kristiansund',
    radius: 100,
  },
  {
    lat: 69.64,
    lng: 19.01,
    city: 'Tromsdalen',
    name: 'Tromsdalen',
    radius: 100,
  },
  {
    lat: 68.44,
    lng: 17.42,
    city: 'Narvik',
    name: 'Narvik',
    radius: 100,
  },
  {
    lat: 64.01,
    lng: 11.49,
    city: 'Steinkjer',
    name: 'Steinkjer',
    radius: 100,
  },
  {
    lat: 59.41,
    lng: 5.27,
    city: 'Haugesund',
    name: 'Haugesund',
    radius: 100,
  },
  {
    lat: 69.97,
    lng: 23.27,
    city: 'Alta',
    name: 'Alta',
    radius: 100,
  },
];

export const getLocationSet = () =>
  getMarket().marketCode === 'NO' ? getLocationSetNO() : getLocationSetSE();
